import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { FileText, Download, Trash2, Upload, Lock } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Label } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { BTN, DOC_CATS, MEDICAL, downloadBlob } from "./hrUtils";

const MAX = 10 * 1024 * 1024;

export const HrDocuments = ({ empId }) => {
  const { user } = useAuth();
  const canMedical = ["owner", "hr"].includes(user?.role);
  const [docs, setDocs] = useState([]);
  const [f, setF] = useState({ category: "contrat", title: "", expiry_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => api.get(`/hr/employees/${empId}/documents`).then((r) => setDocs(r.data)).catch((e) => toast.error(apiErr(e))), [empId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Choisissez un fichier"); return; }
    if (file.size > MAX) { toast.error("Fichier trop volumineux (10 Mo maximum)"); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("category", f.category); fd.append("title", f.title); fd.append("expiry_date", f.expiry_date);
    setBusy(true);
    try {
      await api.post(`/hr/employees/${empId}/documents`, fd, { timeout: 120000 });
      toast.success("Document ajouté"); setFile(null); setF({ category: "contrat", title: "", expiry_date: "" });
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Supprimer « ${d.title} » ?`)) return;
    try { await api.delete(`/hr/documents/${d.id}`); toast.success("Document supprimé"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const dl = (d) => downloadBlob(`/hr/documents/${d.id}/download`, d.original_filename || d.title).catch((e) => toast.error(apiErr(e)));
  const cats = Object.entries(DOC_CATS).filter(([k]) => canMedical || !MEDICAL.includes(k));

  return (
    <div className="mt-3 space-y-3" data-testid="rh-docs">
      <div className="space-y-2 rounded-2xl bg-gray-50 p-3">
        <Label className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Ajouter un document privé</Label>
        <div className="grid grid-cols-2 gap-2">
          <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
            <SelectTrigger data-testid="rh-doc-cat" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{cats.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="rh-doc-title" placeholder="Intitulé" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-xl" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><p className="text-xs text-gray-500">Date d'expiration (facultatif)</p><Input data-testid="rh-doc-expiry" type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div><p className="text-xs text-gray-500">Fichier (PDF, image, Word · 10 Mo max)</p><input ref={inputRef} data-testid="rh-doc-file" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1 w-full text-xs" /></div>
        </div>
        {MEDICAL.includes(f.category) && <p className="text-xs text-amber-600">Document médical : visible uniquement par le propriétaire et le rôle RH.</p>}
        <Button data-testid="rh-doc-upload" disabled={busy} onClick={upload} className={`w-full ${BTN}`}><Upload className="mr-1 h-4 w-4" />{busy ? "Envoi en cours…" : "Envoyer le document"}</Button>
      </div>
      {docs.length === 0 ? <p className="text-sm text-gray-400">Aucun document.</p> : (
        <div className="divide-y divide-gray-50">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 py-2" data-testid={`rh-doc-${d.id}`}>
              <FileText className="h-4 w-4 shrink-0 text-[#065F46]" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{d.title}</p>
                <p className="text-xs text-gray-400">{DOC_CATS[d.category] || d.category} · {Math.round(d.size / 1024)} Ko · {fmtDate(d.created_at)}{d.expiry_date ? ` · expire le ${fmtDate(d.expiry_date)}` : ""}</p>
              </div>
              <button data-testid={`rh-doc-dl-${d.id}`} onClick={() => dl(d)} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100"><Download className="h-4 w-4" /></button>
              <button data-testid={`rh-doc-del-${d.id}`} onClick={() => remove(d)} className="rounded-lg p-1.5 text-rose-500 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
